/** Procedural, deterministic bedding straw that can be varied per placement. */
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { surface } from '../materials';
import type { AssetDefinition } from '../types';

/** Deterministic 0..1 from an integer. A seeded factory is still a pure one. */
function hash(n: number): number {
  let x = (n ^ 0x9e3779b9) >>> 0;
  x = Math.imul(x ^ (x >>> 16), 0x21f0aaad) >>> 0;
  x = Math.imul(x ^ (x >>> 15), 0x735a2d97) >>> 0;
  return ((x ^ (x >>> 15)) >>> 0) / 4294967296;
}

function darken(colour: number, factor: number): number {
  const red = Math.round(((colour >>> 16) & 0xff) * factor);
  const green = Math.round(((colour >>> 8) & 0xff) * factor);
  const blue = Math.round((colour & 0xff) * factor);
  return (red << 16) | (green << 8) | blue;
}

/**
 * Build a shallow, varied tangle from a seed.
 *
 * Straw deliberately uses organic rather than fabric: fabric is Rosalie's
 * monotonic wear surface, so using it here would make the bedding age with her.
 */
export function strawClump(seed: number, tint?: number): THREE.Group {
  const group = new THREE.Group();
  group.name = 'straw-clump';

  const colours = tint === undefined
    ? [0xbcaa79, 0xa8955f, 0x8e7f57] as const
    : [tint, darken(tint, 0.88), darken(tint, 0.74)] as const;
  const materials = colours.map((colour) => surface('organic', colour));
  const geometries = [[], [], []] as THREE.BufferGeometry[][];
  const layoutBounds = new THREE.Box3();

  for (let i = 0; i < 32; i += 1) {
    const hashBase = seed + i * 7;
    const length = 0.1 + hash(hashBase + 0) * 0.16;
    const width = 0.006 + hash(hashBase + 1) * 0.005;
    const height = 0.003 + hash(hashBase + 2) * 0.002;
    const yaw = hash(hashBase + 3) * Math.PI * 2;
    const tilt = i === 0 ? 0 : hash(hashBase + 4) - 0.5;
    // Twenty-one varied stalks, plus the flat contact stalk, occupy the lowest
    // third so the pile reads as trodden bedding rather than a fluffed nest.
    const baseHeight = i === 0 ? 0 : i <= 21
      ? hash(hashBase + 5) * (0.052 / 3)
      : (0.052 / 3) + hash(hashBase + 5) * (0.052 * 2 / 3);
    const radialLimit = 0.2 - Math.hypot(length / 2, width / 2);
    const radialDistance = Math.sqrt(hash(hashBase + 6)) * radialLimit;
    const radialAngle = hash(hashBase + 7) * Math.PI * 2;
    const x = radialDistance * Math.cos(radialAngle);
    const z = radialDistance * Math.sin(radialAngle);
    const verticalExtent = height * Math.cos(Math.abs(tilt)) + width * Math.sin(Math.abs(tilt));
    const y = baseHeight + verticalExtent / 2;
    const geometry = new THREE.BoxGeometry(length, height, width);
    const rotation = new THREE.Quaternion().setFromEuler(new THREE.Euler(tilt, yaw, 0, 'YXZ'));
    geometry.applyMatrix4(new THREE.Matrix4().compose(new THREE.Vector3(x, y, z), rotation, new THREE.Vector3(1, 1, 1)));
    geometry.computeBoundingBox();
    const geometryBounds = geometry.boundingBox;
    if (!geometryBounds) throw new Error('straw-clump: stalk geometry has no bounds');
    layoutBounds.union(geometryBounds);
    const bucket = geometries[i % 3];
    if (!bucket) throw new Error('straw-clump: missing material bucket');
    bucket.push(geometry);
  }

  // The varied deterministic layout has no privileged direction; translating the
  // complete tangle keeps its placement origin honest without disturbing its layers.
  const centring = new THREE.Matrix4().makeTranslation(
    -(layoutBounds.min.x + layoutBounds.max.x) / 2,
    0,
    -(layoutBounds.min.z + layoutBounds.max.z) / 2,
  );
  for (const bucket of geometries) {
    for (const geometry of bucket) geometry.applyMatrix4(centring);
  }

  for (let i = 0; i < geometries.length; i += 1) {
    const bucket = geometries[i];
    const material = materials[i];
    if (!bucket || !material) throw new Error('straw-clump: missing merged surface');
    const merged = mergeGeometries(bucket, false);
    if (!merged) throw new Error('straw-clump: stalk geometries could not be merged');
    for (const geometry of bucket) geometry.dispose();
    const mesh = new THREE.Mesh(merged, material);
    mesh.name = `straw-${i + 1}`;
    mesh.castShadow = true;
    // At this scale 32 overlapping stalks self-shadow as unresolved dirt.
    mesh.receiveShadow = false;
    group.add(mesh);
  }

  return group;
}

export const strawClumpAsset: AssetDefinition = {
  id: 'straw-clump',
  label: 'Straw clump',
  description: 'Trodden layered bedding straw, deterministic and seed-variant.',
  // Measured: the tallest stalk lands at 0.055, not the 0.075 the brief guessed.
  footprint: { width: 0.34, height: 0.056, length: 0.34 },
  create: () => strawClump(0),
};
